import React from 'react';
import {
  Box,
  Typography,
  Paper
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';
import { useSectionStyles } from '@/styles/utilities/hooks';

/**
 * Debug dialog for displaying computed differences (overrides and deletions)
 * This is a developer tool for inspecting the actual JSON that will be sent to the API
 */
const InputspecDebugDialog = ({
  open,
  onClose,
  overrides,
  deletions,
  groupOrder
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  
  // Format the debug information
  const formatDebugInfo = () => {
    const sections = [];
    
    if (overrides && Object.keys(overrides).length > 0) {
      sections.push({
        title: 'OVERRIDES',
        content: JSON.stringify(overrides, null, 2)
      });
    }
    
    if (deletions && Object.keys(deletions).length > 0) {
      sections.push({
        title: 'DELETIONS',
        content: JSON.stringify(deletions, null, 2)
      });
    }
    
    if (groupOrder && Object.keys(groupOrder).length > 0) {
      sections.push({
        title: 'GROUP ORDER',
        content: JSON.stringify(groupOrder, null, 2)
      });
    }
    
    if (sections.length === 0) {
      sections.push({
        title: 'NO CHANGES',
        content: 'No differences detected from the base configuration.'
      });
    }
    
    return sections;
  };
  
  const debugSections = formatDebugInfo();
  
  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title="Configuration Debug Information"
      variant="info"
      width={theme.spacing(70)} // 560px in comfortable density
      height="auto"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          This debug view shows the exact JSON that will be sent to the API when creating the custom configuration.
        </Typography>
        
        {debugSections.map((section, index) => (
          <Box key={index}>
            <Typography variant="caption" sx={sectionStyles.sectionHeader}>
              {section.title}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: theme.spacing(1),
                backgroundColor: theme.palette.grey[900],
                maxHeight: theme.spacing(30),
                overflow: 'auto',
                mt: theme.spacing(0.5)
              }}
            >
              <Typography
                component="pre"
                sx={{
                  fontSize: theme.typography.fontSize.xs,
                  fontFamily: 'monospace',
                  margin: 0,
                  color: section.title === 'NO CHANGES' 
                    ? theme.palette.text.secondary 
                    : theme.palette.info.light,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {section.content}
              </Typography>
            </Paper>
          </Box>
        ))}
      </Box>
    </DraggableDialog>
  );
};

export default InputspecDebugDialog;